export const useTeams = defineStore('teams', () => {
  const store = useBaseStore('teams')

  const user = useUser()
  const config = useRuntimeConfig()
  const isFeatureEnabled = computed(() => config.public.TEAMS_DASHBOARD_ENABLED === 'true')

  const rolesMap = {
    owner: 'Manager',
    billing: 'Billing',
    member: 'Team Member',
    partner: 'Partner'
  }
  const rolesOrder = ['partner', 'billing', 'owner', 'member']

  function getUserRoles (_user) {
    return rolesOrder.filter(role => _user.roles.includes(role)).map(role => rolesMap[role])
  }

  const partner = ref(null)
  const isOnboardingComplete = computed(() => partner.value?.onboarding_status)

  const activeTeams = ref([])
  function fillActiveTeams () {
    let teams
    if (partner.value) {
      teams = partner.value?.teams?.filter((t) => t.status === 'active') || []
    } else {
      teams = store.list.value?.filter((t) => t.status === 'active') || []
    }

    const ids = new Set()
    const uniqueTeams = []
    for (const team of teams) {
      if (!ids.has(team.id)) {
        ids.add(team.id)
        uniqueTeams.push(team)
      }
    }
    activeTeams.value = uniqueTeams
  }

  const loadingTeam = ref(true)
  const selectedTeam = ref(null)
  const selectedTeamMembers = ref([])
  const selectedTeamInvites = ref([])

  const { $api } = useNuxtApp()

  async function getDanglingVouchers () {
    try {
      return await $api.get('/voucher-combos')
    } catch (e) {
      console.error(e)
    }
  }

  async function getTeamVouchers (id) {
    try {
      return await $api.get(`/teams/${id}/voucher-combos`)
    } catch (e) {
      console.error(e)
    }
  }

  async function getPartnerVouchers () {
    try {
      return await $api.get('/team-partner-vouchers')
    } catch (e) {
      console.error(e)
    }
  }

  async function getSelectedTeamVouchers () {
    if (!selectedTeam.value) return []

    try {
      const vouchersList = await getTeamVouchers(selectedTeam.value?.uuid)

      const teamVouchers = vouchersList?.data.map((t) => Object.assign(t, { team_name: vouchersList.team_name, team_uuid: vouchersList.team_uuid })) || []

      return teamVouchers
    } catch (e) {
      console.error(e)
      return []
    }
  }

  async function assignVoucher (teamId, voucherCode, userId) {
    return await $api.post(`/teams/${teamId}/voucher-combos/assign`, {
      voucher_combo_code: voucherCode,
      user_id: userId
    })
  }

  async function assignVoucherToTeam (teamId, voucherCode) {
    return await $api.post(`/teams/${teamId}/voucher-combos/assign-to-team`, {
      voucher_combo_code: voucherCode
    })
  }

  async function getUserPartners () {
    try {
      if (!user.isPartner) return

      const partnersResp = await $api.get('/partners')
      partner.value = partnersResp?.data
    } catch (e) {
      console.error('Error loading partners:', e)
    }
  }

  function fillSelectedTeamUsers (team) {
    const ids = []
    const uniqueUsers = []

    if (!team.data?.users) {
      return
    }

    team.data.users.forEach((_user) => {
      if (!ids.includes(_user.id)) {
        ids.push(_user.id)
        uniqueUsers.push(_user)
      }
    })

    selectedTeamMembers.value = uniqueUsers
  }
  async function selectTeam (id) {
    try {
      loadingTeam.value = true
      if (id) {
        const team = await getTeam(id)
        selectedTeam.value = team.data

        if (team.data?.uuid) {
          const invites = await getTeamInvitiations(selectedTeam.value?.uuid)
          selectedTeamInvites.value = invites?.data?.filter(i => i.status === 'pending').map((i) => ({
            ...i,
            name: `${i.first_name} ${i.last_name}`,
            roles: i.roles.map((r) => r.fields?.role)
          })) || []
        }

        fillSelectedTeamUsers(team)
      }
    } catch (e) {
      console.error(e)
    } finally {
      loadingTeam.value = false
    }
  }

  async function getTeam (id) {
    return await $api.get(`/teams/${id}`)
  }

  async function create (payload, headers = {}) {
    return await $api.post('/teams', payload, headers)
  }

  async function update (id, payload, headers = {}) {
    return await $api.put(`/teams/${id}`, payload, headers)
  }

  async function addMember (teamId, member) {
    return await $api.post(`/teams/${teamId}/users`, member)
  }

  async function restoreMember (teamId, memberId) {
    return await $api.post(`/teams/${teamId}/users`, {
      user_id: memberId
    })
  }

  async function updateUserRole (teamId, payload) {
    return await $api.put(`/teams/${teamId}/users/role`, payload)
  }

  async function archiveUser (teamId, userId) {
    return await $api.delete(`/teams/${teamId}/users`, {
      user_id: userId
    })
  }

  async function completeOnboading (teamId) {
    return await $api.post(`/teams/${teamId}/onboarding/complete`)
  }

  async function archiveTeam (teamId) {
    return await $api.post(`/teams/${teamId}/archive`)
  }

  async function unarchiveTeam (teamId) {
    return await $api.post(`/teams/${teamId}/unarchive`)
  }

  async function getTeamStats (teamId) {
    return await $api.get(`/teams/${teamId}/stats`)
  }

  async function getTeamInvitiations (teamId) {
    return await $api.get(`/teams/${teamId}/invitations`)
  }

  async function sendVoucherInvite (voucherCode, email) {
    return await $api.post(`/vouchers/${voucherCode}/send`, {
      email
    })
  }

  return {
    ...store,
    partner,
    getTeamStats,
    getTeamInvitiations,
    sendVoucherInvite,
    activeTeams,
    selectedTeam,
    selectedTeamMembers,
    selectedTeamInvites,
    selectTeam,
    loadingTeam,
    getUserPartners,
    getTeamVouchers,
    getPartnerVouchers,
    getSelectedTeamVouchers,
    getDanglingVouchers,
    getUserRoles,
    assignVoucher,
    assignVoucherToTeam,
    isOnboardingComplete,
    create,
    update,
    addMember,
    restoreMember,
    updateUserRole,
    archiveUser,
    completeOnboading,
    archiveTeam,
    unarchiveTeam,
    isFeatureEnabled,
    fillActiveTeams
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useVouchers, import.meta.hot))
}
